import { useState, useEffect } from 'react';
import { getDataFromConfigStore } from '../Network/api';
import {
  RAILS_CONFIG_DATA,
  RAILS_CONFIG_PERSISTED_DATA,
  getDataFromAsynStorage,
  setDataToAsyncStorage,
} from '../../Utils/RailsConstant';
import { CONFIG_STATUS, configKeys, PERSIST_CONFIG_KEYS } from './constants';
import { FETCH_STATUS } from './types';
import isEmpty from 'lodash/isEmpty';
import configFallBack from './configDefaultFallbacks.json';

const CACHE_INTERVAL_TIME = 14400000; // 4 hours in epoch time till milliseconds
const CACHE_EXPIRY_KEY = 'CACHE_EXPIRY_TIME';

let fetchStatus: FETCH_STATUS;
const configWaitingPromiseResolvers: [any?] = [];
let stateConfigData = {};

/*
    ***  HOW TO USE CONFIGSTORE:  ***

  1. Add your cfgKeyName in constants file (./constants) in configKeys object. ---- MANDATORY ----
  2. Call useConfigStore with the configKeyName wherever you want in the funnel.
  3. If you want to want multiple configKey response from configStore, send list of configKey names
    as first parameter and it will return an response object like { cfgKeyName: value}.
  4. If you don't want cached response send second parameter (cacheFirst) as false.
  5. If you can't use {useConfigStore} then you can use {getConfigStore}
    function which takes in same parameters as useConfigStore.
  6. Add configKey Fallback values in 'configDefaultFallbacks.json' file.

    ***  POINTS TO NOTE:  ***

  1. Default fallback value is empty object {}.
  2. On the first render useConfigStore returns fallback value in
  'configDefaultFallbacks.json' till API/cached response arrives.
  3. Currently CACHE expiry time is set to 4 hours.
*/

const useConfigStore = (configKey: String | String[], cacheFirst: boolean = true) => {
  const [data, setData] = useState({});
  useEffect(() => {
    async function fetchConfigData() {
      let newData = {};
      if (fetchStatus === CONFIG_STATUS.FETCHING) {
        const fulfillingPromise = new Promise((resolve) => {
          configWaitingPromiseResolvers.push(resolve);
        });
        fulfillingPromise
          .then(() => {
            newData = formatResponseObject(configKey, stateConfigData);
            setData(newData);
            return newData;
          })
          .catch(() => {
            newData = formatResponseObject(configKey, stateConfigData);
            setData(newData);
            return newData;
          });
      } else {
        if (cacheFirst) {
          newData = await getCachedData(configKey);
        } else {
          newData = await getAPIData(configKey);
        }
        setData(newData);
      }
    }
    fetchConfigData();
  }, [configKey, cacheFirst]);

  return data;
};

export default useConfigStore;

//Must be used called with await
export const getConfigStore = async (configKey: String | String[], cacheFirst: boolean = true) => {
  let newData = {};
  if (!isEmpty(stateConfigData)) {
    newData = formatResponseObject(configKey, stateConfigData);
    return newData;
  }
  if (fetchStatus === CONFIG_STATUS.FETCHING) {
    return new Promise((resolve) => {
      configWaitingPromiseResolvers.push(resolve);
    }).then(() => {
      newData = formatResponseObject(configKey, stateConfigData);
      return newData;
    });
  } else {
    if (cacheFirst) {
      newData = await getCachedData(configKey);
    } else {
      newData = await getAPIData(configKey);
    }
    return newData;
  }
};

//Use this method if you don't want to call with await,
//you might want to use getRailsConfigStoreWaitingPromise
export const getStoredConfigStore = (configKey: String | String[]) => {
  let newData = formatResponseObject(configKey, stateConfigData);
  return newData;
};

//Use method if you want to wait till config store is initialized.
export const getRailsConfigStoreWaitingPromise = (timeout = 5000) => {
  if (!isEmpty(stateConfigData)) {
    return Promise.resolve();
  }
  const timeoutPromise = new Promise((resolve, reject) => {
    setTimeout(reject, timeout);
  });
  const fulfillingPromise = new Promise((resolve) => {
    configWaitingPromiseResolvers.push(resolve);
  });
  getAPIData();
  return Promise.race([fulfillingPromise, timeoutPromise]);
};

//Called from railsInit
export const initRailsConfigStore = async () => {
  stateConfigData = {};
  if (isEmpty(stateConfigData)) {
    getAPIData();
  }
};

const getCachedData = async (configKey: String | String[]) => {
  let response = {};
  const configData = !isEmpty(stateConfigData)
    ? stateConfigData
    : await getDataFromAsynStorage(RAILS_CONFIG_DATA);
  if (Date.now() < configData?.[CACHE_EXPIRY_KEY]) {
    response = formatResponseObject(configKey, configData);
  } else {
    response = getAPIData(configKey);
  }
  return response;
};

const storeTimeSensitiveConfigData = async (configData: any) => {
  const reducedConfigData = PERSIST_CONFIG_KEYS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(configData, key)) {
      acc[key] = configData[key];
    }
    return acc;
  }, {} as Record<string, any>);
  await setDataToAsyncStorage(RAILS_CONFIG_PERSISTED_DATA, reducedConfigData);
};

export const getPersistedConfigData = async (configKey: String) => {
  const persistedConfigData = await getDataFromAsynStorage(RAILS_CONFIG_PERSISTED_DATA);
  return persistedConfigData?.[configKey] || {};
};

const getAPIData = async (configKey?: String | String[]) => {
  let data = configKey ? formatResponseObject(configKey) : {};
  fetchStatus = CONFIG_STATUS.FETCHING;
  try {
    const response = await getDataFromConfigStore(getCfgKeyList(), true);
    if (response?.success && response?.configKeys?.length > 0) {
      const newConfigData = getCfgObject(response?.configKeys);
      storeTimeSensitiveConfigData(newConfigData);
      await setDataToAsyncStorage(RAILS_CONFIG_DATA, newConfigData);
      fetchStatus = CONFIG_STATUS.FETCHED;
      stateConfigData = newConfigData;
      if (configKey) {
        data = formatResponseObject(configKey, newConfigData);
      }
    } else {
      fetchStatus = CONFIG_STATUS.ERROR;
    }
  } catch (error) {
    fetchStatus = CONFIG_STATUS.ERROR;
  }
  configWaitingPromiseResolvers.forEach((resolver) => resolver());
  configWaitingPromiseResolvers.length = 0; // empty the array
  return data;
};

const getCfgKeyList = () => {
  const cfgKeyList: any[] = [];
  Object.keys(configKeys).map((cfgKey: String) => cfgKeyList.push(configKeys[cfgKey]));
  return cfgKeyList;
};

const getCfgObject = (configResponseList: any[]) => {
  const newConfigData = {};
  configResponseList.map((cfgObject: any) =>
    Object.assign(newConfigData, { [cfgObject?.cfgKey]: cfgObject?.value }),
  );
  const cacheExpiryTime = Date.now() + CACHE_INTERVAL_TIME;
  Object.assign(newConfigData, { [CACHE_EXPIRY_KEY]: cacheExpiryTime });
  return newConfigData;
};

const formatResponseObject = (configKey: String | String[], configData?: any) => {
  let newResponse = {};
  if (Array.isArray(configKey)) {
    configKey?.map((cfgKey) =>
      Object.assign(newResponse, {
        [cfgKey]: configData?.[cfgKey] || configFallBack?.[cfgKey] || {},
      }),
    );
  } else {
    newResponse = configData?.[configKey] || configFallBack?.[configKey] || {};
  }
  return newResponse;
};
